/**
 * Lists this tenant's open invitations and revokes them.
 *
 * Settled invitations (accepted or revoked) are history, not an action list,
 * so they never render here at all — mixing them with the open ones would
 * stop this from reading as a list of things an administrator can still do,
 * and the frame draws no toggle for it. An expired-but-unrevoked invitation
 * is still open on the wire (`isOpen` below), and the API still accepts
 * revoking it, so it stays in this list rather than moving to the settled
 * ones -- `isExpired` marks the row with `invite.expired` instead.
 *
 * The `invite.*` catalogue keys are explained in `InviteForm.tsx`'s module
 * doc: a section distinct from the catalogue's older `invitations.*`
 * entries.
 *
 * Revoke is its own `ConfirmDialog`, rendered once per list keyed off which
 * invitation is being revoked, not once per row — window.confirm has no
 * accessible name and no place for the API's own failure to land.
 *
 * One card per invitation, the same shape as `MemberList`'s. There is no resend
 * route anywhere in this API, so no "Reenviar" action is rendered.
 */

import { Inbox } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ConfirmDialog } from '../../../chrome/form';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../../../ui/card';
import type { Invitation } from '../api/schemas';
import { tt } from '../i18n';
import { tm } from '../pages/team.messages';
import { useInvitations, useRevokeInvitation } from './useInvitations';

const INVITATIONS_HEADING_ID = 'invitations-heading';

function isOpen(invitation: Invitation): boolean {
  return invitation.accepted_at === null && invitation.revoked_at === null;
}

function isExpired(invitation: Invitation): boolean {
  return new Date(invitation.expires_at).getTime() <= Date.now();
}

function scopeOf(invitation: Invitation): string {
  return invitation.campaign_id === null
    ? tt('members.scope.tenantWide')
    : (invitation.campaign_name ?? invitation.campaign_id);
}

/** "enviado há N dias" / "sent N days ago" -- `Intl.RelativeTimeFormat`
 *  rounds to whole days and gives the locale's own "hoje"/"ontem" at the
 *  boundary rather than "0 dias atrás". */
function sentAgo(iso: string, locale: string): string {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-days, 'day');
}

export function InvitationList(props: { tenantId: string }): ReactNode {
  const { tenantId } = props;
  const locale = getLocale();
  const invitations = useInvitations(tenantId);
  const revoke = useRevokeInvitation(tenantId);
  const [revoking, setRevoking] = useState<Invitation | null>(null);

  const visible = (invitations.data ?? []).filter(isOpen);

  return (
    <section aria-labelledby={INVITATIONS_HEADING_ID} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 id={INVITATIONS_HEADING_ID} className="text-title-md">
          {tt('invitations.heading')}
        </h2>
        {invitations.data !== undefined && <Badge variant="secondary">{visible.length}</Badge>}
      </div>
      <QueryStates query={invitations} empty={<EmptyState title={tm('invitations.empty')} />}>
        {() =>
          visible.length === 0 ? (
            <EmptyState title={tm('invitations.empty')} />
          ) : (
            <ul
              aria-labelledby={INVITATIONS_HEADING_ID}
              className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {visible.map((invitation) => (
                <li key={invitation.id}>
                  <Card className="h-full gap-3 py-4">
                    <CardHeader className="flex flex-row items-center gap-3 px-4">
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <Inbox aria-hidden className="size-4" />
                      </span>
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="truncate font-medium" title={invitation.email}>
                          {invitation.email}
                        </span>
                        {isExpired(invitation) && (
                          <Badge variant="outline">{tm('invite.expired')}</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-center gap-2 px-4">
                      <Badge variant="secondary">{tt(`role.short.${invitation.role}`)}</Badge>
                      <Badge variant="outline">{scopeOf(invitation)}</Badge>
                      <span className="text-caption text-muted-foreground">
                        {sentAgo(invitation.created_at, locale)}
                      </span>
                    </CardContent>
                    <CardFooter className="px-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setRevoking(invitation)}
                      >
                        {tt('invite.revoke')}
                      </Button>
                    </CardFooter>
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </QueryStates>
      {revoking !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
          title={tm('revoke.title', { email: revoking.email })}
          description={tt('invitations.revoke.confirm')}
          confirmLabel={tm('revoke.confirm')}
          destructive
          pending={revoke.isPending}
          failure={revoke.error?.failure ?? null}
          onConfirm={() => revoke.mutateAsync({ invitationId: revoking.id })}
        />
      )}
    </section>
  );
}
